import { http } from '../plugins/http'
import { type AxiosResponse } from 'axios'
import DataResponse from '@/types/DataResponse'

import type PaginatedResponse from '@/types/PaginatedResponse'
import Order from '@/types/Order'
import OrderItem from '@/types/OrderItem'

interface MetadataResponse {
  sales: Record<'Confirmado' | 'Cancelado' | 'Recusado' | 'Cortesia', number>
  tickets: Record<'Confirmado' | 'Cancelado' | 'Recusado' | 'Cortesia', number>
  total: Record<'Confirmado' | 'Cancelado' | 'Recusado' | 'Cortesia', number>
}

export default class OrderService {
  async index(
    page: number,
    filters: string,
    per_page: number | undefined = 15,
  ): Promise<AxiosResponse<PaginatedResponse<Order> & { meta: { aggregates: string[] } }>> {
    const _filters = new URLSearchParams()

    filters.split('&').forEach((pair) => {
      const [key, value] = pair.split('=')
      if (value) _filters.set(key, value)
    }) // prevents 'null' at the backend

    return await http.get<PaginatedResponse<Order> & { meta: { aggregates: string[] } }>(
      `/api/v1/office/orders?page=${page}&${_filters}&per_page=${per_page}`,
    )
  }

  async metadata(
    filters: string,
  ): Promise<AxiosResponse<MetadataResponse>> {
    const _filters = new URLSearchParams()

    filters.split('&').forEach((pair) => {
      const [key, value] = pair.split('=')
      if (value) _filters.set(key, value)
    }) // prevents 'null' at the backend

    return await http.get<MetadataResponse>(
      `/api/v1/office/orders/metadata?${_filters}`,
    )
  }

  async chargeback(
    id: string,
  ): Promise<AxiosResponse<DataResponse<Order>>> {
    return await http.post<DataResponse<Order>>(
      `/api/v1/office/orders/${id}/chargeback`,
    )
  }

  async void(
    id: string,
  ): Promise<AxiosResponse<DataResponse<Order>>> {
    return await http.post<DataResponse<Order>>(
      `/api/v1/office/orders/${id}/void`,
    )
  }

  async cancel(
    id: string,
  ): Promise<AxiosResponse<DataResponse<Order>>> {
    return await http.delete<DataResponse<Order>>(
      `/api/v1/office/orders/${id}`,
    )
  }

  async patchHolder(
    id: string,
    data: { name: string, document: string, document_type?: 'cpf' | 'passport' },
  ) {
    return await http.patch(`/api/v1/office/sales/items/${id}/holder`, data)
  }

  async changeType(
    id: string,
    ticket_type_id: string,
  ): Promise<AxiosResponse<DataResponse<OrderItem>>> {
    return await http.patch<DataResponse<OrderItem>>(
      `/api/v1/office/sales/items/${id}/type`,
      { ticket_type_id },
    )
  }

  async show(
    id: string,
  ): Promise<AxiosResponse<DataResponse<Order>>> {
    return await http.get<DataResponse<Order>>(
      `/api/v1/office/orders/${id}`,
    )
  }

  async items(
    page: number,
    filters: string,
    per_page: number | undefined = 10,
    event_id: string,
  ): Promise<AxiosResponse<PaginatedResponse<unknown>>> {
    return await http.get<PaginatedResponse<unknown>>(
      `/api/v1/office/orders/${event_id}/report?page=${page}&${filters}&per_page=${per_page}`,
    )
  }

  async report(
    filters: string,
    selected: Record<string, string>,
  ): Promise<AxiosResponse<BlobPart>> {
    filters = filters !== '' ? `?${filters}` : ''
    return await http.post(
      `/api/v1/office/orders/report${filters}`,
      { selected },
      {
        headers: {
          Accept: 'application/csv',
        },
      },
    )
  }
}
